import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import BabylonViewer from 'components/answers/FileEmbed/BabylonViewer'
import {
    fetchBsvOrdinalPreview,
    uploadBsvOrdinalPreviewImage,
    fetchUrlPreviewByAddress,
    uploadUrlPreviewImage,
} from 'api/api'
import type {
    OrdinalPreviewDispatchDecision,
    OrdinalPreviewReadyOptions,
} from 'components/shared/ordinalPreviewDispatch'
import type { OrdinalPreviewResponse } from 'types/urls'
import { Rotate3D, Maximize2 } from 'lucide-react'
import styles from './ordinal-3d-loader.module.scss'
import {
    ORDINAL_3D_BACKGROUND_CAPTURE_TIMEOUT_MS,
    reserveOrdinal3DBackgroundCapture,
    shouldStartOrdinal3DBackgroundCapture,
    subscribeOrdinal3DBackgroundCaptureSlot,
} from './ordinal3dPreviewPolicy'

interface Ordinal3DLoaderProps {
    src: string
    bsvOrdinalId?: string
    className?: string
    allowInteraction?: boolean
    previewUrl?: string | null
    /**
     * Opt-in preview capture. Stream/gallery tiles that omit this remain cheap
     * placeholders; opted-in callers share a bounded one-at-a-time capture slot.
     */
    autoCapturePreview?: boolean
    forceLive?: boolean
    onRequestLive?: () => void
    onPreviewPending?: () => void
    onPreviewReady?: (options?: OrdinalPreviewReadyOptions) => void
    fromLinkPreview?: boolean
    previewDispatchDecision?: OrdinalPreviewDispatchDecision
}

const Ordinal3DLoader: React.FC<Ordinal3DLoaderProps> = ({
    src,
    bsvOrdinalId,
    className,
    allowInteraction = true,
    previewUrl,
    autoCapturePreview = false,
    forceLive = false,
    onRequestLive,
    onPreviewPending,
    onPreviewReady,
    fromLinkPreview = false,
}) => {
    const uploadRef = useRef<boolean>(false)
    const previewFailedRef = useRef<boolean>(false)
    const backgroundCaptureActiveRef = useRef<boolean>(false)
    const backgroundCaptureReleaseRef = useRef<(() => void) | null>(null)
    const backgroundCaptureTimeoutRef = useRef<number | null>(null)
    const [resolvedPreviewUrl, setResolvedPreviewUrl] = useState<string | null>(previewUrl || null)
    const [showViewer, setShowViewer] = useState<boolean>(forceLive)
    const [backgroundCaptureSlotTick, setBackgroundCaptureSlotTick] = useState<number>(0)
    // Tracks explicit user intent (click/tap) to keep the viewer open.
    const userActivatedRef = useRef<boolean>(forceLive)
    const prevSrcRef = useRef<string | null>(null)
    const backgroundCaptureKey = useMemo(
        () => (bsvOrdinalId ? `bsv:${bsvOrdinalId}` : `src:${src}`),
        [bsvOrdinalId, src],
    )

    const clearBackgroundCaptureTimeout = useCallback(() => {
        if (!backgroundCaptureTimeoutRef.current) return
        window.clearTimeout(backgroundCaptureTimeoutRef.current)
        backgroundCaptureTimeoutRef.current = null
    }, [])

    const finishBackgroundCapture = useCallback(() => {
        if (!backgroundCaptureActiveRef.current) return

        backgroundCaptureActiveRef.current = false
        clearBackgroundCaptureTimeout()
        const release = backgroundCaptureReleaseRef.current
        backgroundCaptureReleaseRef.current = null
        release?.()

        if (!forceLive && !userActivatedRef.current) {
            setShowViewer(false)
        }
    }, [clearBackgroundCaptureTimeout, forceLive])

    useEffect(() => {
        if (prevSrcRef.current === src) return
        prevSrcRef.current = src
        previewFailedRef.current = false
        uploadRef.current = false
        userActivatedRef.current = forceLive
        setShowViewer(forceLive)
        setResolvedPreviewUrl(previewUrl || null)
    }, [src, previewUrl, forceLive])

    useEffect(() => {
        if (forceLive) {
            userActivatedRef.current = true
            setShowViewer(true)
        }
    }, [forceLive])

    // Do not auto-mount Babylon for missing previews. Stream/gallery tiles should remain cheap
    // placeholders unless a caller explicitly requests a bounded background capture.
    useEffect(() => {
        if (forceLive) return
        if (userActivatedRef.current) return
        if (backgroundCaptureActiveRef.current) return
        setShowViewer(false)
    }, [autoCapturePreview, forceLive, resolvedPreviewUrl])

    useEffect(() => {
        if (
            !shouldStartOrdinal3DBackgroundCapture({
                autoCapturePreview,
                hasPreview: !!resolvedPreviewUrl,
                forceLive,
                userActivated: userActivatedRef.current,
                backgroundCaptureActive: backgroundCaptureActiveRef.current,
            })
        ) {
            return
        }

        let started = false
        const release = reserveOrdinal3DBackgroundCapture(backgroundCaptureKey)
        if (release) {
            started = true
            backgroundCaptureActiveRef.current = true
            backgroundCaptureReleaseRef.current = release
            setShowViewer(true)

            clearBackgroundCaptureTimeout()
            backgroundCaptureTimeoutRef.current = window.setTimeout(() => {
                console.warn('Ordinal3DLoader: background preview capture timed out', {
                    src,
                    bsvOrdinalId,
                })
                finishBackgroundCapture()
            }, ORDINAL_3D_BACKGROUND_CAPTURE_TIMEOUT_MS)
        }

        const unsubscribe = subscribeOrdinal3DBackgroundCaptureSlot(() => {
            setBackgroundCaptureSlotTick(tick => tick + 1)
        })

        return () => {
            unsubscribe()
            if (started) finishBackgroundCapture()
        }
    }, [
        autoCapturePreview,
        backgroundCaptureKey,
        backgroundCaptureSlotTick,
        bsvOrdinalId,
        clearBackgroundCaptureTimeout,
        finishBackgroundCapture,
        forceLive,
        resolvedPreviewUrl,
        src,
    ])

    useEffect(() => {
        if (!previewUrl) {
            setResolvedPreviewUrl(null)
            return
        }
        previewFailedRef.current = false
        setResolvedPreviewUrl(previewUrl)
        if (!forceLive && !userActivatedRef.current) {
            setShowViewer(false)
        }
        onPreviewReady?.({ previewUrl, source: 'prop' })
    }, [previewUrl, forceLive, onPreviewReady])

    useEffect(() => {
        if (forceLive || !src || resolvedPreviewUrl) return
        let cancelled = false

        ;(async () => {
            try {
                const existing: OrdinalPreviewResponse | null = bsvOrdinalId
                    ? await fetchBsvOrdinalPreview(bsvOrdinalId)
                    : await fetchUrlPreviewByAddress(src)
                if (cancelled) return
                const preview = existing?.preview_url || existing?.preview_image_url || null
                if (existing?.ok && preview) {
                    setResolvedPreviewUrl(preview)
                    if (!forceLive && !userActivatedRef.current) {
                        setShowViewer(false)
                    }
                    onPreviewReady?.({ previewUrl: preview, source: 'existing' })
                    return
                }
                onPreviewPending?.()
            } catch (e) {
                // ignore lookup failures
            }
        })()

        return () => {
            cancelled = true
        }
    }, [src, resolvedPreviewUrl, forceLive, onPreviewPending, onPreviewReady])

    const handlePreviewGenerated = useCallback(
        async (blob: Blob) => {
            if (!blob || !src) return
            if (uploadRef.current) return
            uploadRef.current = true
            try {
                const res: OrdinalPreviewResponse | null = bsvOrdinalId
                    ? await uploadBsvOrdinalPreviewImage(bsvOrdinalId, blob)
                    : await uploadUrlPreviewImage(src, blob)

                const preview = res?.preview_url || res?.preview_image_url || null
                if (res?.ok && preview) {
                    setResolvedPreviewUrl(preview)
                    // If we auto-loaded purely to capture a preview, hide the viewer once we have one.
                    if (!forceLive && !userActivatedRef.current) {
                        setShowViewer(false)
                    }
                    onPreviewReady?.({ previewUrl: preview, source: 'generated' })
                }
            } catch (e) {
                uploadRef.current = false
                console.warn('Ordinal3DLoader: failed to upload preview image', e)
            } finally {
                finishBackgroundCapture()
            }
        },
        [src, bsvOrdinalId, forceLive, finishBackgroundCapture, onPreviewReady],
    )

    const handleRequestLive = useCallback((event: React.MouseEvent | React.TouchEvent) => {
        event.stopPropagation()
        userActivatedRef.current = true
        setShowViewer(true)
    }, [])

    const handlePreviewImageError = useCallback(
        (event: React.SyntheticEvent<HTMLImageElement>) => {
            // If the stored preview URL is stale (e.g., dangling ActiveStorage blob / S3 NoSuchKey),
            // fall back to the cheap placeholder rather than auto-mounting Babylon in the feed.
            try {
                event.stopPropagation()
            } catch {}
            if (previewFailedRef.current) return
            previewFailedRef.current = true
            // Allow re-upload even if a previous session succeeded.
            uploadRef.current = false
            setResolvedPreviewUrl(null)
            try {
                onPreviewPending?.()
            } catch {}
            userActivatedRef.current = false
            setShowViewer(false)
        },
        [onPreviewPending],
    )

    const handleMaximizeClick = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation()
            event.preventDefault()
            onRequestLive?.()
        },
        [onRequestLive],
    )

    const shouldShowPreview = resolvedPreviewUrl && !showViewer
    const shouldShowPlaceholder = !resolvedPreviewUrl && !showViewer
    const shouldShowMaximizeHint = showViewer && !allowInteraction && !!onRequestLive

    const file = useMemo(
        () => ({
            name: 'ordinal.glb',
            url: src,
            type: 'model/gltf-binary',
            size: 0,
        }),
        [src],
    )

    return (
        <div
            className={cn(styles.wrapper, className, fromLinkPreview && styles.inThread)}
            data-no-modal-interaction
            data-post-embed-navigation-exempt="true"
        >
            {shouldShowPreview && (
                <div
                    className={styles.previewImageContainer}
                    data-no-modal-interaction
                    onClick={handleRequestLive}
                >
                    <img
                        src={resolvedPreviewUrl as string}
                        alt="Ordinal preview"
                        className={styles.previewImage}
                        loading="lazy"
                        decoding="async"
                        onError={handlePreviewImageError}
                    />
                    <button
                        type="button"
                        className={styles.overlayButton}
                        onClick={handleRequestLive}
                        onMouseDown={event => event.stopPropagation()}
                        onPointerDown={event => event.stopPropagation()}
                        onTouchStart={handleRequestLive}
                        aria-label="Load 3D preview"
                        title="Load 3D preview"
                    >
                        <Rotate3D size={26} strokeWidth={2.4} />
                    </button>
                </div>
            )}

            {shouldShowPlaceholder && (
                <div
                    className={cn(styles.previewImageContainer, styles.placeholder)}
                    data-no-modal-interaction
                    onClick={handleRequestLive}
                >
                    <button
                        type="button"
                        className={styles.overlayButton}
                        onClick={handleRequestLive}
                        onMouseDown={event => event.stopPropagation()}
                        onPointerDown={event => event.stopPropagation()}
                        onTouchStart={handleRequestLive}
                        aria-label="Load 3D model"
                        title="Load 3D model"
                    >
                        <Rotate3D size={26} strokeWidth={2.4} />
                    </button>
                    <span className={styles.placeholderHint}>Load 3D model</span>
                </div>
            )}

            {showViewer && (
                <>
                    <div
                        className={styles.viewerContainer}
                        data-no-modal-interaction
                        style={{ pointerEvents: allowInteraction || showViewer ? 'auto' : 'none' }}
                        onClick={event => event.stopPropagation()}
                        onMouseDown={event => event.stopPropagation()}
                        onPointerDown={event => event.stopPropagation()}
                        onTouchStart={event => event.stopPropagation()}
                    >
                        <BabylonViewer
                            file={file}
                            height={'100%'}
                            capturePreview={!resolvedPreviewUrl}
                            captureAspects={['1x1']}
                            captureResolution={2}
                            onPreviewGenerated={handlePreviewGenerated}
                            onPreviewFailed={error => {
                                uploadRef.current = false
                                if (backgroundCaptureActiveRef.current) {
                                    console.warn(
                                        'Ordinal3DLoader: background preview capture failed',
                                        error,
                                    )
                                }
                                finishBackgroundCapture()
                            }}
                        />
                    </div>
                    {shouldShowMaximizeHint && (
                        <button
                            type="button"
                            className={styles.maximizeHint}
                            onClick={handleMaximizeClick}
                            aria-label="Open full-screen viewer"
                            title="Open full-screen viewer"
                        >
                            <Maximize2 size={18} strokeWidth={2.2} />
                        </button>
                    )}
                </>
            )}
        </div>
    )
}

export default React.memo(Ordinal3DLoader)
